'use client';
import { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';

export default function LoginPage() {
  const router = useRouter();
  const { signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email || !password) {
      setError('Please enter both email and password.');
      return;
    }

    setLoading(true);
    try {
      await signIn(email, password);
      // Fetch role from profiles table to redirect correctly
      const supabase = createClient();
      const { data: { user } } = await supabase.auth.getUser();
      let redirectPath = '/portal/dashboard';
      if (user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('role')
          .eq('id', user.id)
          .single();
        if (profile?.role === 'admin') {
          redirectPath = '/admin';
        } else if (profile?.role === 'team_member') {
          redirectPath = '/portal/team/dashboard';
        }
      }
      router.push(redirectPath);
    } catch (err: any) {
      setError(err?.message || 'Invalid email or password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-16">
      {/* Background accent */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-primary/5 blur-[120px] rounded-full" />
      </div>

      <div className="relative w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-12">
          <Link href="/">
            <AppImage
              src="/assets/images/boc_logo_240x90_white-1771394304209.png"
              alt="Best Odds Corp Logo"
              width={200}
              height={75}
              className="mx-auto w-[180px] h-auto mb-8"
            />
          </Link>
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-primary/10 border border-primary/20 mb-6">
            <div className="w-1.5 h-1.5 bg-primary rounded-full" />
            <span className="text-micro uppercase tracking-widest text-primary">Client Portal</span>
          </div>
          <h1 className="text-h3 mb-2">Welcome Back</h1>
          <p className="text-body-sm text-muted-foreground">Sign in to access your dashboard</p>
        </div>

        {/* Form Card */}
        <div className="bg-surface-1 border border-border p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email */}
            <div>
              <label className="block text-label uppercase tracking-widest text-muted-foreground mb-2">
                Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@company.com"
                className="w-full bg-surface-2 border border-border px-4 py-3 text-body-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary transition-colors"
                autoComplete="email"
                disabled={loading}
              />
            </div>

            {/* Password */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-label uppercase tracking-widest text-muted-foreground">
                  Password
                </label>
                <Link
                  href="/forgot-password"
                  className="text-micro uppercase tracking-widest text-muted-foreground hover:text-primary transition-colors"
                >
                  Forgot Password?
                </Link>
              </div>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="w-full bg-surface-2 border border-border px-4 py-3 pr-12 text-body-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary transition-colors"
                  autoComplete="current-password"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  tabIndex={-1}
                >
                  <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={18} />
                </button>
              </div>
            </div>

            {/* Error */}
            {error && (
              <div className="flex items-start gap-3 p-4 bg-error/10 border border-error/30">
                <Icon name="ExclamationCircleIcon" size={18} className="text-error mt-0.5 shrink-0" />
                <p className="text-label text-error">{error}</p>
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-primary hover:bg-primary-hover text-white py-4 text-label uppercase tracking-widest transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Signing In...
                </>
              ) : (
                'Sign In'
              )}
            </button>
          </form>
        </div>

        {/* Footer links */}
        <div className="mt-8 text-center space-y-4">
          <p className="text-body-sm text-muted-foreground">
            New client?{' '}
            <Link href="/book" className="text-primary hover:text-primary-hover transition-colors">
              Apply to work with us
            </Link>
          </p>
          <Link
            href="/"
            className="block text-micro uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors"
          >
            ← Back to Main Site
          </Link>
        </div>
      </div>
    </div>
  );
}
